// Bookings scheduler — view registry, date maths and colour resolution.
//
// This is the v6 port of the legacy dhtmlxScheduler configured in
// mod_flight_booking/includes/flight_booking_new_display.inc (2341 lines),
// fed by flight_booking_data.inc via
//   ajaxproxy.html?function=flight_booking&mode=ajax&get_booking_data_json=yes
// The widget itself is NOT ported; only its data contract and view geometry,
// rendered with plain CSS grid so the module carries no new dependency.
//
// EVERY CONFIG DEFAULT BELOW WAS MEASURED, not assumed — `config` rows with
// config_function='flight_booking' on module_g2_demo, 2026-08-29 (154 rows).
// The live values are read through useConfig at runtime; the constants here
// are only the fallback for a tenant that has not set them.

import { format } from "date-fns";

import type { BoardToolbarViewGroup } from "../components/BoardToolbar";
import {
  EVENT_TONES,
  EventTone,
  NO_TIMES_TEXT,
  allDayBarTitle,
  eventBlockHtml,
  timelineBarHtml,
  toneForIndex,
} from "../components/schedulerBoard";
import type { FlightBookingColor } from "./bookings";
import type { Booking } from "./types";

// ---------------------------------------------------------------- views

export interface CalendarView {
  /** Toolbar label, verbatim from the legacy selects. */
  label: string;
  /** What the VIEW button caps to when this view is selected. */
  short: string;
  /** Which of the legacy's three dropdown FAMILIES this view belongs to. */
  family: "calendar" | "timeline" | "other";
  /** Days shown per page. */
  days: number;
  /**
   * Grid orientation.
   *  "day-columns"         — day columns x hour rows (legacy Calendar views)
   *  "section-rows"        — aircraft rows x hour columns (legacy timeline)
   *  "section-columns"     — aircraft columns x hour rows (legacy "unit"/Vertical)
   *  "section-day-columns" — aircraft rows x DAY columns (legacy timeline_week;
   *                          the Gantt geometry). A DIFFERENT renderer from
   *                          "section-rows", not a parameter on it: that one's
   *                          x-axis is hours and it reads only days[0].
   */
  orientation:
    | "day-columns"
    | "section-rows"
    | "section-columns"
    | "section-day-columns";
  /**
   * Legacy `load_from_mode` value this view maps to, for provenance. EMPTY
   * means the view has no legacy counterpart — don't read "" as "unknown",
   * read it as "v6-only, so legacy behaviour cannot be cited for it".
   */
  legacyMode: string;
  /**
   * Drag-to-move is OFF for this view, and the reason is never cosmetic —
   * see the `timeline_4week` entry and MONTH_VIEW_DEFERRED. A view whose
   * legacy write branch we have not ported must not offer the gesture, or
   * v6 writes where legacy would not.
   */
  dragDisabled?: boolean;
}

/**
 * The legacy view set. Keys are the persisted values (see modes.ts
 * readRememberedCalendarView) so they must stay stable.
 *
 * `timeline_month` ("Month") is DEFERRED — see MONTH_VIEW_DEFERRED below.
 * `grid` ("Agenda") is not here: in v6 that surface is this module's List
 * route, which already renders the same columns.
 */
export const CALENDAR_VIEWS: { [key: string]: CalendarView } = {
  day: {
    label: "Calendar Day",
    short: "Day",
    family: "calendar",
    days: 1,
    orientation: "day-columns",
    legacyMode: "day",
  },
  workweek: {
    label: "Calendar 4 Day",
    short: "4 Days",
    family: "calendar",
    days: 4,
    orientation: "day-columns",
    legacyMode: "timeline_workweek",
  },
  week: {
    label: "Calendar Week",
    short: "Week",
    family: "calendar",
    days: 7,
    orientation: "day-columns",
    legacyMode: "timeline_week",
  },
  timeline: {
    label: "Aircraft Timeline",
    short: "Aircraft Day",
    family: "timeline",
    days: 1,
    orientation: "section-rows",
    legacyMode: "timeline",
  },
  // ─── The Gantt family. Aircraft ROWS x DAY columns ────────────────────
  //
  // WHY THESE ARE NEW KEYS RATHER THAN A FIX TO `workweek`/`week`. Those two
  // carry legacyMode "timeline_workweek"/"timeline_week" but orientation
  // "day-columns", i.e. legacy's AIRCRAFT-ROW views were migrated into
  // calendar day-column views and the section axis was lost. The keys are
  // PERSISTED (modes.ts readRememberedCalendarView), so re-pointing them
  // would silently change the view under every operator who last used one.
  // The calendar-family views are legitimate surfaces in their own right and
  // Aaron's screenshot shows one in use, so they stay exactly as they are and
  // the aircraft-row geometry lands here.
  timeline_workweek: {
    label: "Timeline 4 Day",
    short: "Timeline 4 Days",
    family: "timeline",
    days: 4,
    orientation: "section-day-columns",
    legacyMode: "timeline_workweek",
  },
  timeline_week: {
    label: "Timeline Week",
    short: "Timeline Week",
    family: "timeline",
    days: 7,
    orientation: "section-day-columns",
    legacyMode: "timeline_week",
  },
  /** Four-week timeline uses the ordinary booking schedule/assignment writes.
   * It has no legacy counterpart; the user explicitly requested editing this
   * view. It does not inherit the deferred legacy month special case. */
  timeline_4week: {
    label: "Timeline 4 Weeks",
    short: "Timeline 4 Weeks",
    family: "timeline",
    days: 28,
    orientation: "section-day-columns",
    legacyMode: "",
  },
  unit: {
    label: "Vertical",
    short: "Vertical",
    family: "other",
    days: 1,
    orientation: "section-columns",
    legacyMode: "unit",
  },
};

/**
 * Legacy `timeline_month`. STILL NOT implemented, and the reason is
 * DATA INTEGRITY, not rendering — "the month grid is a different renderer"
 * (this comment's original wording) undersells it, because as of this pass
 * the day-cell renderer EXISTS: `section-day-columns` above, which
 * `timeline_4week` already uses over a 28-day span. So the renderer is no
 * longer the blocker. The write branch is.
 *
 * MEASURED at flight_booking_ajax.inc:3431-3450. In `timeline_month`, when
 * the dragged booking is multi-day, the UPDATE writes only:
 *     modified_by, asset_type, modified
 * — no `date`, no `date_to`, no `start_time`, no `stop_time`. Every other
 * view falls through to the `<else>` at :3444, which writes all four. So a
 * month-view drag in legacy RE-SECTIONS a multi-day booking and deliberately
 * leaves its dates alone. Ship month cells wired to the ordinary drop
 * handler and v6 would MOVE bookings legacy does not move — a silent write
 * divergence on customer data, not a cosmetic gap.
 *
 * `timeline_4week` is therefore not this view: it carries no legacyMode and
 * uses the ordinary schedule writes explicitly requested for this view. Porting `timeline_month` properly means porting
 * that branch first.
 */
export const MONTH_VIEW_DEFERRED = "timeline_month";

export const isCalendarView = (key: string): boolean =>
  Object.prototype.hasOwnProperty.call(CALENDAR_VIEWS, key);

/** Grouped for the toolbar's three separate selects, as in the screenshot. */
export const VIEW_FAMILIES: {
  key: CalendarView["family"];
  label: string;
}[] = [
  { key: "calendar", label: "Calendar" },
  { key: "timeline", label: "Timeline" },
  { key: "other", label: "Other" },
];

/**
 * Which view families each LISTING MODE owns.
 *
 * The timeline views were reachable only through the toolbar's View select,
 * two levels down from the breadcrumb, so the module's headline Gantt was
 * effectively hidden. They are now a top-level mode (modes.ts MODE_LABELS
 * `timeline`), and this map is what each mode's select offers.
 *
 * WHY `other` RIDES WITH TIMELINE. Filtering each mode strictly to its own
 * family would leave the "other" family — legacy `unit`, labelled "Vertical" —
 * reachable from neither mode, i.e. a real legacy view silently retired by a
 * navigation change. It is an aircraft-SECTION view ("section-columns"), like
 * the timeline family and unlike the day-axis calendar family, so it groups
 * here. VIEW_FAMILIES still supplies its own subheader, so it reads as its own
 * group inside the Timeline select rather than being passed off as a timeline.
 */
export const MODE_VIEW_FAMILIES: {
  [mode: string]: CalendarView["family"][];
} = {
  calendar: ["calendar"],
  timeline: ["timeline", "other"],
};

/** Is `key` a view offered by `mode`? */
export function isViewInMode(key: string, mode: string): boolean {
  if (!isCalendarView(key)) return false;
  const families = MODE_VIEW_FAMILIES[mode] ?? MODE_VIEW_FAMILIES.calendar;
  return families.includes(CALENDAR_VIEWS[key].family);
}

/**
 * The VIEW menu as Pattern 441 sections: one group per family this mode owns,
 * in family order, options in `CALENDAR_VIEWS` key order. Empty groups drop.
 */
export function viewGroupsForMode(mode: string): BoardToolbarViewGroup[] {
  const families = MODE_VIEW_FAMILIES[mode] ?? MODE_VIEW_FAMILIES.calendar;
  return VIEW_FAMILIES.filter((fam) => families.includes(fam.key))
    .map((fam) => ({
      label: fam.label,
      options: Object.entries(CALENDAR_VIEWS)
        .filter(([, v]) => v.family === fam.key)
        .map(([key, v]) => ({ key, label: v.label, short: v.short })),
    }))
    .filter((g) => g.options.length > 0);
}

// ---------------------------------------------------------------- config

/** config flight_booking_first_hour — measured 8 on module_g2_demo. */
export const DEFAULT_FIRST_HOUR = 8;
/** config flight_booking_last_hour — measured 21 on module_g2_demo. */
export const DEFAULT_LAST_HOUR = 21;
/** config flight_booking_default_view — measured "workweek" on module_g2_demo. */
export const DEFAULT_VIEW = "workweek";
/**
 * The view a mode opens on when nothing valid is remembered for it.
 *
 * Declared AFTER DEFAULT_VIEW deliberately: it reads that binding at module
 * evaluation, so sitting above it would hit the temporal dead zone and throw
 * "Cannot access 'DEFAULT_VIEW' before initialization" on import — a crash on
 * load, not a type error.
 *
 * Calendar keeps DEFAULT_VIEW ("workweek"), the value measured in the tenant's
 * own config. Timeline opens on `timeline` ("Aircraft Timeline") — the legacy
 * `load_from_mode=timeline`, and the only view in the family with a 1:1 legacy
 * counterpart, so it is the one choice here that cites legacy rather than
 * taste.
 */
export const MODE_DEFAULT_VIEW: { [mode: string]: string } = {
  calendar: DEFAULT_VIEW,
  timeline: "timeline",
};
/**
 * config flight_booking_default_note_color_code — measured "#fff8af".
 * Applied to the all-day banner rows, mirroring new_display.inc L603-605.
 */
export const DEFAULT_NOTE_COLOR = "#fff8af";

/**
 * Legacy `source_type` marking a booking row as an ALL-DAY NOTE rather than a
 * flight booking. flight_booking_data.inc L384 keeps these rows in the feed
 * unconditionally (they bypass the booking-id filter) and L603 recolours them;
 * they are the free-text banner spanning a day in the reference screenshot.
 * SAME TABLE as a booking — not a separate note store.
 */
export const NOTE_SOURCE_TYPES = ["edit_note", "edit_contract"];

export const isNoteRow = (b: Booking): boolean =>
  NOTE_SOURCE_TYPES.includes(String(b.source_type ?? ""));

// ------------------------------------------------------- section axis

/**
 * THE LEGACY TIMELINE SECTION AXIS IS THREE-PART, MEASURED at
 * flight_booking_new_display.inc:401-445:
 *
 *   1. an UNCONDITIONAL row keyed `aircraft-0`, labelled verbatim
 *      "Notes / No Aircraft"  (L401-406)
 *   2. aircraft rows                                        (L407-424)
 *   3. ASSET-TYPE rows from `types`                          (L429-444)
 *
 * ⚠️ "W and B" IN THE REFERENCE SCREENSHOT IS PART 3, NOT PART 1. It is an
 * asset type — a GRANDCHILD of config `flight_booking_asset_type_parent_id`
 * (the legacy query is `parent_id IN (SELECT id FROM types WHERE parent_id =
 * <config>)`), on the tenant that screenshot came from. It is NOT a special
 * row and must never be hardcoded.
 *
 * WHY PART 3 IS ABSENT HERE, stated rather than silently skipped. Measured on
 * module_g2_demo 2026-08-30: that config is 6974, whose children are Aircraft
 * (6975, active) / Classroom (6976, inactive) / Project (6977, inactive), and
 * whose GRANDCHILDREN number ZERO. So g2 has no asset-type sections at all
 * and rendering part 3 would add nothing visible on this tenant. Fetching
 * them needs a grandchildren read — `useTypes` returns one level, so it is an
 * N+1 over the children — which is not worth adding to a render-critical
 * component for a tenant with none. A one-LEVEL read is NOT an acceptable
 * shortcut: it would render "Aircraft / Classroom / Project" as sections,
 * and a wrong label is worse than a missing one.
 *
 * Part 2 also diverges deliberately: legacy reads its aircraft axis from
 * `flight_booking_aircraft` ORDER BY that table's own `priority`, which has
 * ZERO active rows on module_g2_demo — legacy would render an empty axis.
 * The v6 views use the tenant's real fleet (`useAircraftOptions`) instead.
 */
export const NOTE_SECTION_ID = 0;
export const NOTE_SECTION_LABEL = "Notes / No Aircraft";

export interface TimelineSection {
  /** `NOTE_SECTION_ID` for the notes row, else the aircraft id. */
  id: number;
  label: string;
  /** The notes row takes note rows AND bookings with no aircraft. */
  isNoteSection: boolean;
}

/**
 * Section axis for the aircraft-row views: the notes row first, then the
 * fleet in the order the caller supplies (which must be the SAME order
 * `resolveBookingColor` is given, or the colours stop following the aircraft).
 */
export function buildSections(
  fleet: { id: number; registration?: string | null }[]
): TimelineSection[] {
  return [
    { id: NOTE_SECTION_ID, label: NOTE_SECTION_LABEL, isNoteSection: true },
    ...fleet.map((a) => ({
      id: a.id,
      label: String(a.registration ?? `#${a.id}`),
      isNoteSection: false,
    })),
  ];
}

/**
 * Does this row belong in this section? The notes section is the catch-all
 * legacy makes it: an all-day note OR a booking with no aircraft assigned.
 * Without the second half those bookings would be fetched, filtered, counted
 * — and then rendered nowhere, which reads as data loss.
 */
export function inSection(b: Booking, section: TimelineSection): boolean {
  const acId = b.aircraft_obj?.id ?? null;
  return section.isNoteSection ? isNoteRow(b) || acId == null : acId === section.id;
}

// ---------------------------------------------------------------- colour

/**
 * Block palette matching GCH live fleet colors (`flight_booking_colors` on `module_gch_demo`):
 *   1: ZK-HDG -> #02B82C (Green, white font)
 *   2: ZK-HFH -> #FF1605 (Red, white font)
 *   3: ZK-IDX -> #2919FF (Blue, white font)
 *   4: ZK-IXX -> #0F0F0F (Black, white font)
 *   5: ZK-KVZ -> #FC00A0 (Pink/Magenta, white font)
 * Default unassigned bookings: #018a90 (Aeronet Teal)
 */
export const BLOCK_PALETTE = [
  "#02B82C",
  "#FF1605",
  "#2919FF",
  "#0F0F0F",
  "#FC00A0",
  "#01aeab",
  "#EBB86C",
  "#67BCC9",
  "#A091AD",
  "#CCAC61",
];

export const DEFAULT_BOOKING_COLOR = "#01aeab";
export const DEFAULT_BOOKING_TEXT_COLOR = "#FFFFFF";

// No per-tenant literals here. GCH's fleet colours (aircraft ids 1-5,
// ZK-HDG…ZK-KVZ) used to be baked in and applied to EVERY tenant's aircraft
// with those ids (GUI sweep 2026-09-25 §1). Colour comes from this tenant's
// /v6/bookings/colors rows, else the palette by position on the section axis.

/**
 * Colour for one block. Returns live aircraft colour, or default teal for unassigned bookings.
 */
export function blockColor(
  aircraftId: number | null | undefined,
  sectionIds: number[]
): string {
  if (aircraftId == null) return DEFAULT_BOOKING_COLOR;
  const idx = sectionIds.indexOf(aircraftId);
  if (idx < 0) return DEFAULT_BOOKING_COLOR;
  return BLOCK_PALETTE[idx % BLOCK_PALETTE.length];
}

function isDarkColor(hex: string): boolean {
  const clean = hex.replace("#", "");
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16);
    const g = parseInt(clean[1] + clean[1], 16);
    const b = parseInt(clean[2] + clean[2], 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 140;
  }
  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 140;
  }
  return false;
}

export interface BookingColorResult {
  color: string;
  textColor?: string;
}

/**
 * Resolves event background and font colors according to tenant configuration:
 *  - source_type 'edit_note' -> noteColor (default #fff8af)
 *  - mode 'aircraft' -> database flight_booking_colors mapped by aircraft_id / registration
 *  - mode 'status' -> database flight_booking_colors mapped by status_id
 *  - mode 'type' -> database flight_booking_colors mapped by booking_type_id / flight_type_id
 *  - fallback -> the flights tone for the aircraft's section position
 */
/** The rows `resolveBookingColor` paints with the tenant's note colour. */
export const isNoteColored = (booking: {
  source_type?: string | null;
}): boolean =>
  booking.source_type === "edit_note" ||
  booking.source_type === "note" ||
  (booking as any).is_note === true;

export function resolveBookingColor(
  booking: {
    source_type?: string | null;
    aircraft_id?: number | null;
    aircraft_obj?: { id: number; registration?: string | null } | null;
    status_id?: number | null;
    type_id?: number | null;
    booking_type?: number | null;
  },
  sectionIds: number[],
  colorsList?: FlightBookingColor[] | null,
  colorMode: string = "aircraft",
  noteColor: string = "#fff8af"
): BookingColorResult {
  if (isNoteColored(booking)) {
    return { color: noteColor, textColor: "#000000" };
  }

  const aircraftId = booking.aircraft_obj?.id ?? booking.aircraft_id;

  if (colorMode === "aircraft") {
    // 1. Check colorsList from API
    if (colorsList && colorsList.length > 0 && aircraftId != null) {
      const match = colorsList.find(
        (c) =>
          Number(c.aircraft_id) === Number(aircraftId) &&
          Boolean(c.bg_color) &&
          c.bg_color !== "#"
      );
      if (match && match.bg_color) {
        const textColor =
          match.font_color && match.font_color !== "#"
            ? match.font_color
            : isDarkColor(match.bg_color)
            ? "#FFFFFF"
            : "#000000";
        return { color: match.bg_color, textColor };
      }
    }

    // 3. If no aircraft assigned, return canonical live default (Teal #01aeab with white text)
    if (aircraftId == null) {
      return { color: DEFAULT_BOOKING_COLOR, textColor: DEFAULT_BOOKING_TEXT_COLOR };
    }
  } else if (colorMode === "status" && booking.status_id != null) {
    if (colorsList && colorsList.length > 0) {
      const match = colorsList.find(
        (c) =>
          Number(c.status_id) === Number(booking.status_id) &&
          Boolean(c.bg_color) &&
          c.bg_color !== "#"
      );
      if (match && match.bg_color) {
        const textColor =
          match.font_color && match.font_color !== "#"
            ? match.font_color
            : isDarkColor(match.bg_color)
            ? "#FFFFFF"
            : "#000000";
        return { color: match.bg_color, textColor };
      }
    }
  } else if (colorMode === "type" && (booking.booking_type != null || booking.type_id != null)) {
    const typeId = booking.booking_type ?? booking.type_id;
    if (colorsList && colorsList.length > 0) {
      const match = colorsList.find(
        (c) =>
          (Number(c.booking_type_id) === Number(typeId) ||
            Number(c.flight_type_id) === Number(typeId)) &&
          Boolean(c.bg_color) &&
          c.bg_color !== "#"
      );
      if (match && match.bg_color) {
        const textColor =
          match.font_color && match.font_color !== "#"
            ? match.font_color
            : isDarkColor(match.bg_color)
            ? "#FFFFFF"
            : "#000000";
        return { color: match.bg_color, textColor };
      }
    }
  }

  // No tenant colour for this row: take the flights tone for the aircraft's
  // position on the section axis, so the two boards colour identically.
  const tone = toneForIndex(
    aircraftId == null ? -1 : sectionIds.indexOf(aircraftId)
  );
  return { color: tone.accent, textColor: tone.text };
}

/** Note rows keep the legacy yellow; this is its accent / title colour. */
export const NOTE_TONE_ACCENT = "#c9a800";
export const NOTE_TONE_TEXT = "#5a4a00";

/** Drag-created events have no booking yet, so no tenant colour either. */
const DRAFT_TONE: EventTone = toneForIndex(0);

/**
 * Tenant colour -> Figma tone. `resolveBookingColor` hands back a background
 * and a font colour, but the font colour only carries tenant intent when the
 * tenant actually set `font_color`; otherwise it is the black/white contrast
 * default, and the Figma block wants its accent for the title (like flights).
 */
export function bookingTone(
  booking: { source_type?: string | null },
  resolved: BookingColorResult,
  noteColor: string = DEFAULT_NOTE_COLOR
): EventTone {
  if (isNoteColored(booking)) {
    return { accent: NOTE_TONE_ACCENT, tint: noteColor, text: NOTE_TONE_TEXT };
  }
  const accent = resolved.color;
  // The palette fallback already IS a flights tone — hand back the whole tone
  // so the tint is the Figma solid, pixel-identical to a flight block.
  const exact = EVENT_TONES.find(
    (t) => t.accent.toLowerCase() === accent.toLowerCase()
  );
  if (exact) return exact;
  if (accent.toLowerCase() === DEFAULT_BOOKING_COLOR.toLowerCase()) {
    return toneForIndex(0);
  }

  const font = String(resolved.textColor ?? "").toLowerCase();
  const explicit = Boolean(font) && font !== "#ffffff" && font !== "#000000";
  return {
    accent,
    // ponytail: color-mix, Chrome/Edge 111+ — the whole board already needs it.
    tint: `color-mix(in srgb, ${accent} 16%, white)`,
    // A tenant bg_color with no font_color would otherwise put a light colour
    // on its own 16% tint; darken it towards the body text instead.
    text: explicit
      ? String(resolved.textColor)
      : `color-mix(in srgb, ${accent} 70%, #212121)`,
  };
}

/** Block title: booking name, reference, registration, client, else whatever the event carries. */
export function bookingEventTitle(event: any): string {
  const b: Booking | undefined = event?.rawBooking;
  if (!b) return String(event?.text || "New booking");
  const prefix =
    b.parent_id && Number(b.parent_id) > 0 ? `${b.parent_id}/${b.id} ` : "";
  const name =
    b.booking_name ??
    b.reference ??
    b.aircraft_obj?.registration ??
    b.client_company?.name ??
    event?.text ??
    `#${b.id}`;
  return `${prefix}${name}`;
}

/** `event_text` for the day-column views (Figma Component 68). */
export function bookingBlockText(start: Date, end: Date, event: any): string {
  const b: Booking | undefined = event?.rawBooking;
  const hasFlights = Boolean(b?.flight_lines && b.flight_lines.length > 0);
  return eventBlockHtml({
    title: bookingEventTitle(event),
    id: b?.id ? `# ${b.id}` : undefined,
    time: `${format(start, "HH:mm")} - ${format(end, "HH:mm")}`,
    tone: event?.tone ?? DRAFT_TONE,
    hasFlights,
  });
}

/**
 * `event_bar_text` for the timeline views (Figma Pattern 810) and the all-day
 * row. An all-day booking (no recorded times) says so: on its time line where
 * the bar has one, in its title where the bar is one line.
 */
export function bookingBarText(
  start: Date,
  end: Date,
  event: any,
  withTime: boolean
): string {
  const b: Booking | undefined = event?.rawBooking;
  const hasFlights = Boolean(b?.flight_lines && b.flight_lines.length > 0);
  const title = bookingEventTitle(event);
  if (event?.allDay) {
    return timelineBarHtml({
      title: withTime ? title : allDayBarTitle(title),
      time: withTime ? NO_TIMES_TEXT : undefined,
      tone: event?.tone ?? DRAFT_TONE,
      hasFlights,
    });
  }
  return timelineBarHtml({
    title,
    time: withTime
      ? `${format(start, "HH:mm")} \u2013 ${format(end, "HH:mm")}`
      : undefined,
    tone: event?.tone ?? DRAFT_TONE,
    hasFlights,
  });
}

// ---------------------------------------------------------------- dates

/** "YYYY-MM-DD" for a Date, in LOCAL time (never toISOString — that is UTC). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Inclusive booking finish, shared by geometry and writes. Legacy records
 * may have an unset or backwards date_to while date_finish is usable. */
export function bookingEndDate(b: Booking): string {
  const validDay = (value: unknown): string => {
    const iso = String(value ?? "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || iso === "0000-00-00") return "";
    const parsed = new Date(`${iso}T00:00:00`);
    return !Number.isNaN(parsed.getTime()) && toIsoDate(parsed) === iso ? iso : "";
  };
  const start = validDay(b.date);
  if (!start) return "";
  const to = validDay(b.date_to);
  if (to && to >= start) return to;
  const finish = validDay(b.date_finish);
  return finish && finish >= start ? finish : start;
}

export function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function addDays(iso: string, n: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + n);
  return toIsoDate(d);
}

/**
 * The visible date range for a view anchored on `anchor`.
 *
 * `>= 7` week-aligns to MONDAY — DHTMLX's `start_on_monday` default paints the
 * week Mon–Sun — so the fetched range, the toolbar label and the painted
 * columns are the same days. It used to align to Sunday (`-d.getDay()`), which
 * left the displayed Sunday outside the fetch. Day and 4-Day start ON the
 * anchor date, which is not a week boundary.
 *
 * `>= 7`, not `=== 7`: the multi-week Gantt spans 28 days and must align to
 * the same boundary or its columns sit under four PARTIAL weeks and the
 * weekend banding reads as noise.
 */
export function viewRange(view: CalendarView, anchor: string): string[] {
  let start = anchor;
  if (view.days >= 7) {
    const d = parseIsoDate(anchor);
    start = addDays(anchor, -((d.getDay() + 6) % 7));
  }
  return Array.from({ length: view.days }, (_, i) => addDays(start, i));
}

/** First visible day of the view — what the scheduler must be anchored on. */
export function viewStart(view: CalendarView, anchor: string): string {
  return viewRange(view, anchor)[0];
}

/** First and last visible day, for the toolbar period label and the fetch. */
export function viewSpan(
  view: CalendarView,
  anchor: string
): { start: string; end: string } {
  const days = viewRange(view, anchor);
  return { start: days[0], end: days[days.length - 1] };
}

/**
 * Column header for a day in the Gantt — "Sun 30". Short weekday + day of
 * month is what the reference screenshot's Sun..Sat header carries, and it
 * stays legible at 28 columns where a full date would not.
 */
export function dayColumnLabel(iso: string): { weekday: string; day: string } {
  const d = parseIsoDate(iso);
  return {
    weekday: d.toLocaleDateString("en-NZ", { weekday: "short" }),
    day: String(d.getDate()),
  };
}

/** Saturday or Sunday — banded in the Gantt so week boundaries are readable. */
export function isWeekend(iso: string): boolean {
  const wd = parseIsoDate(iso).getDay();
  return wd === 0 || wd === 6;
}

/**
 * Which column index holds today, or -1 when today is outside the span. The
 * reference screenshot draws a today marker line; this is what positions it.
 */
export function todayIndex(days: string[]): number {
  return days.indexOf(toIsoDate(new Date()));
}

/**
 * The [firstIndex, lastIndex] columns a booking occupies inside `days`, or
 * null when it occupies none of them.
 *
 * This is what makes the view a GANTT rather than a grid of per-day chips: a
 * booking spanning Mon..Thu is ONE bar across four columns. Derived by
 * scanning `occupiesDate` over the span rather than by date arithmetic, so
 * the zero-date handling in `occupiesDate` (legacy stores an unset end as
 * "0000-00-00") is applied in exactly one place.
 */
export function dayIndexSpan(
  b: Booking,
  days: string[]
): { from: number; to: number } | null {
  let from = -1;
  let to = -1;
  for (let i = 0; i < days.length; i++) {
    if (!occupiesDate(b, days[i])) continue;
    if (from < 0) from = i;
    to = i;
  }
  return from < 0 ? null : { from, to };
}

// ------------------------------------------------------- event geometry

/** "HH:MM[:SS]" -> minutes since midnight. Null for an unparseable value. */
export function toMinutes(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
}

export function hhmm(hhmmish: string | null | undefined): string {
  const mins = toMinutes(hhmmish);
  if (mins == null) return "";
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

/** The "HH:MM" inside a datetime-shaped value ("YYYY-MM-DD HH:MM[:SS]"), else "". */
function timeInDatetime(v: unknown): string {
  const s = String(v ?? "");
  return s.length >= 16 ? hhmm(s.slice(11, 16)) : "";
}

/**
 * Does the booking carry ANY recorded time — a start or stop time, or a time
 * inside a datetime-shaped `date` / `date_to` / `date_finish` (the same
 * sources the board reads its block times from)? A booking with none is an
 * ALL-DAY event (Aaron, 2026-09-30, H252: "should be all day events"), not a
 * block stretched across the visible hours.
 */
export function bookingHasTimes(b: Booking): boolean {
  return Boolean(
    hhmm(b.start_time) ||
      hhmm(b.stop_time) ||
      timeInDatetime(b.date) ||
      timeInDatetime(b.date_to) ||
      timeInDatetime((b as any).date_finish)
  );
}

/**
 * Fractional position of a booking inside the visible hour window, as
 * [start, end] in 0..1. Returns null when the booking has no usable time —
 * a timeless booking is rendered in the all-day band, NOT clamped to 00:00,
 * which would put every one of them in the top-left cell.
 */
export function timeSpan(
  b: Booking,
  firstHour: number,
  lastHour: number
): { top: number; height: number } | null {
  const start = toMinutes(b.start_time);
  const stop = toMinutes(b.stop_time);
  if (start == null) return null;
  const windowStart = firstHour * 60;
  const windowEnd = lastHour * 60;
  const span = windowEnd - windowStart;
  if (span <= 0) return null;
  // A stop at or before the start (legacy allows both blank and equal) still
  // gets a readable block rather than a zero-height sliver.
  const end = stop != null && stop > start ? stop : start + 30;
  const top = (Math.max(start, windowStart) - windowStart) / span;
  const height = (Math.min(end, windowEnd) - Math.max(start, windowStart)) / span;
  if (height <= 0) return null;
  return { top, height: Math.max(height, 0.012) };
}

/**
 * Does this booking occupy `isoDate`? Legacy multi-day bookings carry
 * date..date_to (`multi_day: true` in new_display.inc), so a block must
 * appear on every day in that span, not only its start date.
 */
export function occupiesDate(b: Booking, isoDate: string): boolean {
  const from = String(b.date ?? "").slice(0, 10);
  if (!from) return false;
  const to = bookingEndDate(b);
  if (!to) return false;
  return isoDate >= from && isoDate <= to;
}

/**
 * Block label, matching the legacy text in the reference screenshot:
 *   "09:00 - 13:30 / 167078 / 179190 ZK-HBC Pending Company ABC"
 * i.e. time range / booking id / flight id, registration, status, client.
 * Every part is omitted when absent rather than rendered as a placeholder.
 */
export function blockLabel(b: Booking): string {
  const times = [hhmm(b.start_time), hhmm(b.stop_time)].filter(Boolean).join(" - ");
  const flightIds = (b.flight_lines ?? []).map((l) => l.flight_id).join(" ");
  const flightIndicator = flightIds ? `✈ ${flightIds}` : "";
  const idDisplay = (b as any).parent_id ? `${(b as any).parent_id} / ${b.id}` : String(b.id);
  const head = [times, idDisplay, flightIndicator].filter(Boolean).join(" / ");
  const isMx = b.source_type === "edit_job";
  const tail = [
    isMx ? "[Maintenance]" : "",
    b.aircraft_obj?.registration,
    b.status_obj?.name,
    b.client_company?.name ?? b.booking_name,
  ]
    .filter(Boolean)
    .join(" ");
  return [head, tail].filter(Boolean).join(" ");
}
